import React, { useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Button, Flex, Popconfirm, Space, Tag, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import TicketAssignmentsContext from '@totem/components/ticketing/assignments/TicketAssignmentsContext';
import {
  PARTY_TYPE_ASSIGNEE,
  PARTY_TYPE_WATCHER,
  TICKET_ASSIGNMENT_STATUS_OPTIONS,
  TicketAssignment,
  TicketAssignmentAssignee,
} from '@totem/components/ticketing/assignments/types';
import { ASSIGNEE_TYPE_LABELS } from '@totem/components/ticketing/templateManagement/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { FilterField } from '@totem/components/ui_components/reportSettings/types';

const GLOBAL_SCOPE = 'All Organizations';

const uniqueSorted = (values: (string | undefined)[]): string[] =>
  Array.from(new Set(values.filter((value): value is string => !!value))).sort(
    (left, right) => left.localeCompare(right),
  );

const selectedValues = (filters: FilterField[], key: string): string[] =>
  filters
    .find((filter) => filter.key === key)
    ?.values?.map((val) => val.value) ?? [];

const scopeName = (record: TicketAssignment) =>
  [
    record.hierarchy?.organization?.name,
    record.hierarchy?.region?.name,
    record.hierarchy?.building?.name,
  ]
    .filter((name): name is string => !!name)
    .join(' / ');

const partyTypeColor = (partyType: string) => {
  if (partyType === PARTY_TYPE_ASSIGNEE) return 'blue';
  if (partyType === PARTY_TYPE_WATCHER) return 'default';
  return 'purple';
};

export const assigneeDescription = (assignee: TicketAssignmentAssignee) => {
  const typeLabel = ASSIGNEE_TYPE_LABELS[assignee.type] ?? assignee.type;
  return assignee.value ? `${typeLabel}: ${assignee.value}` : typeLabel;
};

const compareAssignments = (
  left: TicketAssignment,
  right: TicketAssignment,
  field: string,
): number => {
  switch (field) {
    case 'name':
      return left.name.localeCompare(right.name);
    case 'status':
      return left.status.localeCompare(right.status);
    case 'sequence':
      return left.sequence - right.sequence;
    default:
      return 0;
  }
};

const TicketAssignmentsTable = () => {
  const navigate = useNavigate();
  const { loading, assignments, canEdit, onEdit, onDuplicate, onDelete } =
    useContext(TicketAssignmentsContext);
  // setFilterOptions isn't stable across renders, so it's deliberately left out of the effect deps
  const { input, handleTableChange, setFilterOptions } =
    useContext(InputContext);

  useEffect(() => {
    setFilterOptions(
      'status',
      TICKET_ASSIGNMENT_STATUS_OPTIONS.map((status) => ({
        text: status,
        value: status,
      })),
    );
  }, []);

  useEffect(() => {
    setFilterOptions(
      'ticketType',
      uniqueSorted(assignments.map((record) => record.match.type)).map(
        (value) => ({ text: value, value }),
      ),
    );

    setFilterOptions('organization', [
      { text: GLOBAL_SCOPE, value: GLOBAL_SCOPE },
      ...uniqueSorted(
        assignments.map((record) => record.hierarchy?.organization?.name),
      ).map((value) => ({ text: value, value })),
    ]);

    setFilterOptions(
      'partyType',
      uniqueSorted(
        assignments.flatMap((record) =>
          (record.assignees ?? []).map((assignee) => assignee.partyType),
        ),
      ).map((value) => ({ text: value, value })),
    );
  }, [assignments]);

  const visibleAssignments = useMemo(() => {
    const statusValues = selectedValues(input.filters, 'status');
    const typeValues = selectedValues(input.filters, 'ticketType');
    const organizationValues = selectedValues(input.filters, 'organization');
    const partyTypeValues = selectedValues(input.filters, 'partyType');

    let filtered = assignments;
    if (statusValues.length > 0) {
      filtered = filtered.filter((record) =>
        statusValues.includes(record.status),
      );
    }
    if (typeValues.length > 0) {
      filtered = filtered.filter((record) =>
        typeValues.includes(record.match.type),
      );
    }
    if (organizationValues.length > 0) {
      filtered = filtered.filter((record) =>
        organizationValues.includes(
          record.hierarchy?.organization?.name || GLOBAL_SCOPE,
        ),
      );
    }
    if (partyTypeValues.length > 0) {
      filtered = filtered.filter((record) =>
        (record.assignees ?? []).some((assignee) =>
          partyTypeValues.includes(assignee.partyType),
        ),
      );
    }

    if (!input.sort.field) return filtered;

    const direction = input.sort.direction === 'descend' ? -1 : 1;
    return [...filtered].sort(
      (left, right) =>
        compareAssignments(left, right, input.sort.field) * direction,
    );
  }, [assignments, input.filters, input.sort]);

  const sortOrderFor = (key: string): 'ascend' | 'descend' | null =>
    input.sort.field === key
      ? (input.sort.direction as 'ascend' | 'descend')
      : null;

  const columns: ColumnProps<TicketAssignment>[] = [
    {
      title: 'Seq',
      key: 'sequence',
      width: 80,
      sorter: true,
      sortOrder: sortOrderFor('sequence'),
      render: (_, record) => record.sequence,
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      sorter: true,
      sortOrder: sortOrderFor('status'),
      render: (_, record) => (
        <Tag color={record.status === 'Active' ? 'green' : 'default'}>
          {record.status}
        </Tag>
      ),
    },
    {
      title: 'Name',
      key: 'name',
      width: 240,
      sorter: true,
      sortOrder: sortOrderFor('name'),
      render: (_, record) =>
        record.notes ? (
          <Tooltip title={record.notes}>
            <span>{record.name}</span>
          </Tooltip>
        ) : (
          record.name
        ),
    },
    {
      title: 'Scope',
      key: 'scope',
      width: 170,
      render: (_, record) => scopeName(record) || <Tag>{GLOBAL_SCOPE}</Tag>,
    },
    {
      title: 'Type',
      key: 'type',
      width: 130,
      render: (_, record) => record.match.type || <Tag>Any</Tag>,
    },
    {
      title: 'Category',
      key: 'category',
      width: 130,
      render: (_, record) => record.match.category || <Tag>Any</Tag>,
    },
    {
      title: 'Sub-Category',
      key: 'subCategory',
      width: 160,
      render: (_, record) => record.match.subCategory || <Tag>Any</Tag>,
    },
    {
      title: 'Summary Contains',
      key: 'summaryContains',
      width: 170,
      ellipsis: true,
      render: (_, record) =>
        record.match.summaryContains ? (
          <code>{record.match.summaryContains}</code>
        ) : null,
    },
    {
      title: 'Assignees',
      key: 'assignees',
      width: 340,
      render: (_, record) => (
        <Flex vertical gap={2}>
          {(record.assignees ?? []).map((assignee, index) => (
            <span key={`${assignee.partyType}-${assignee.type}-${index}`}>
              <Tag color={partyTypeColor(assignee.partyType)}>
                {assignee.partyType}
              </Tag>
              {assigneeDescription(assignee)}
            </span>
          ))}
        </Flex>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: canEdit ? 150 : 50,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Rule">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/dashboard/admin/rules/${record.id}`)}
            />
          </Tooltip>
          {canEdit && (
            <>
              <Tooltip title="Edit">
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => onEdit(record)}
                />
              </Tooltip>
              <Tooltip title="Duplicate">
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => onDuplicate(record)}
                />
              </Tooltip>
              <Popconfirm
                title="Delete this ticket assignment?"
                description="Its rule is deleted too. This cannot be undone."
                onConfirm={() => onDelete(record.id)}
                okText="Delete"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Delete">
                  <Button size="small" icon={<DeleteOutlined />} danger />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <IBTable
      rowKey={(record) => record.id}
      columns={columns}
      dataSource={visibleAssignments}
      loading={loading}
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: visibleAssignments.length,
        showSizeChanger: true,
      }}
      onChange={handleTableChange}
    />
  );
};

export default TicketAssignmentsTable;
